import React, {
  ReactNode,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import CriteriaEditPanel from './CriteriaEditPanel';
import { CriteriaPanel } from './CriteriaPanel';
import { useCriteriaDraft } from './useCriteriaDraft';

export type EditableCriteriaHandle = {
  requestClose: () => void;
  isDirty: () => boolean;
};

type HostContent = {
  title: string;
  panel: ReactNode;
  isSaving: boolean;
};

type Props = {
  headingId: string;
  children: (content: HostContent) => ReactNode;
  onCloseConfirmed: () => void;
  onOpenWindow?: () => void;
  pollForUpdates?: boolean;
  onCancelConfirmed?: () => void;
  sectionHeadingComponent?: 'h2' | 'h3';
  // Replaces the view panel, never an open draft; the pop-out passes its
  // session-ended banner.
  banner?: ReactNode;
  emptyState?: ReactNode;
};

export const EditableCriteria = forwardRef<EditableCriteriaHandle, Props>(
  (
    {
      headingId,
      children,
      onCloseConfirmed,
      onOpenWindow,
      pollForUpdates = false,
      onCancelConfirmed,
      sectionHeadingComponent,
      banner,
      emptyState,
    },
    ref
  ) => {
    // Mode switches unmount whichever button held focus, so each transition
    // must place focus deliberately rather than let it fall to <body>.
    const [focusEditOnReturn, setFocusEditOnReturn] = useState(false);
    // The focus intent is one-shot: left set, a panel remount (e.g. the
    // 800px presentation flip) would steal focus to the Edit button again.
    const onFocusEditHandled = useCallback(
      () => setFocusEditOnReturn(false),
      []
    );

    const draft = useCriteriaDraft({
      onExit: (outcome) => {
        if (outcome === 'cancelled') onCancelConfirmed?.();
        if (outcome === 'closed') {
          setFocusEditOnReturn(false);
          onCloseConfirmed();
        } else {
          setFocusEditOnReturn(true);
        }
      },
    });

    // An open draft always wins; the banner (the pop-out's
    // session-ended notice) only ever replaces the view panel.
    const mode = draft.isEditing ? 'edit' : banner != null ? 'banner' : 'view';

    const startCriteriaEdit = () => {
      setFocusEditOnReturn(false);
      draft.start();
    };

    useImperativeHandle(ref, () => ({
      requestClose: () => {
        if (draft.isEditing) {
          draft.requestClose();
        } else {
          setFocusEditOnReturn(false);
          onCloseConfirmed();
        }
      },
      isDirty: () => draft.isDirty,
    }));

    // Entering edit unmounts the focused Edit button; focus moves to the
    // heading rather than dropping to <body>.
    useEffect(() => {
      if (draft.isEditing) {
        document.getElementById(headingId)?.focus();
      }
    }, [draft.isEditing, headingId]);

    // Focus the banner so screen readers announce it (the heading's text is
    // unchanged), and clear the focus intent its replaced panel would consume.
    const bannerRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
      if (mode === 'banner') {
        bannerRef.current?.focus();
        onFocusEditHandled();
      }
    }, [mode, onFocusEditHandled]);

    let panel: ReactNode;
    if (mode === 'edit') {
      panel = <CriteriaEditPanel {...draft.panelProps} />;
    } else if (mode === 'banner') {
      panel = (
        <div tabIndex={-1} ref={bannerRef}>
          {banner}
        </div>
      );
    } else {
      panel = (
        <CriteriaPanel
          autoFocusEdit={focusEditOnReturn}
          onAutoFocusEditHandled={onFocusEditHandled}
          onEnterEdit={startCriteriaEdit}
          onOpenWindow={onOpenWindow}
          pollForUpdates={pollForUpdates}
          sectionHeadingComponent={sectionHeadingComponent}
          emptyState={emptyState}
        />
      );
    }

    const title =
      mode === 'edit' ? 'Edit eligibility criteria' : 'Eligibility criteria';

    return <>{children({ title, panel, isSaving: draft.isSaving })}</>;
  }
);

EditableCriteria.displayName = 'EditableCriteria';
